import React, { FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { scoreCls } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import type { PdpUrlScore } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import { ScoreTrack } from "../../../videos/selectedVideoCardV2/performance/ScoreTrack";
import { ScoreTrend } from "../../../videos/selectedVideoCardV2/performance/ScoreTrend";
import type { TrendPoint } from "../../../videos/selectedVideoCardV2/performance/ScoreTrend";
import { subDelta, trendPoints } from "../../../videos/selectedVideoCardV2/performance/pageScore";
import { BandChip, DeltaChip, Micro, SubScore } from "../components/ui";
import { dayMonth, pageMovement, provenanceLine, scoreStage, trendAriaLabel, trendLabelIndexes } from "./pageModel";
import type { PageView, PdpUrl } from "./pageModel";

/** The block's 11.5px --text-2 captions: the provenance line, "N scores", the note under the score button. */
export const CAPTION = { fontSize: 11.5 } as const;

// A trend label's width in px, from a canvas. Null canvas (jsdom): a
// generous 0.62em a character.
let labelCanvas: CanvasRenderingContext2D | null | undefined;
const labelWidth = (text: string, font: string): number => {
  if (labelCanvas === undefined) {
    try {
      labelCanvas = document.createElement("canvas").getContext("2d");
    } catch {
      labelCanvas = null;
    }
  }
  if (!labelCanvas) return text.length * 6.2;
  labelCanvas.font = font;
  return labelCanvas.measureText(text).width;
};

/** The canvas font a 10px trend label draws in under `host`, with `className` (the last label's). */
const labelFont = (host: HTMLElement, className?: string): string => {
  let el: HTMLElement = host;
  if (className) {
    el = document.createElement("span");
    el.className = className;
    host.appendChild(el);
  }
  const cs = getComputedStyle(el);
  const font = `${cs.fontStyle} ${cs.fontWeight} 10px ${cs.fontFamily}`;
  if (el !== host) el.remove();
  return font;
};

/**
 * The trend with only the x-labels that fit (#11): the shared ScoreTrend's
 * thinning was sized for 3-letter "Sep" labels, and the page's "14 Sep" ones
 * collide — the last two at 1440 with 14 weekly scores. ScoreTrend is not
 * edited: this measures its host (the span, whose width is ScoreTrend's
 * clientWidth) and the labels, and `trendLabelIndexes` picks which to keep;
 * `formatLabel` returns "" for the others, which ScoreTrend draws as an empty
 * <text> (nothing). Re-measured on resize (a hidden tab measures 0 and
 * ScoreTrend's own 160px floor applies) and once the fonts have loaded.
 */
const PageTrend: FC<{ points: TrendPoint[]; overall: number }> = ({ points, overall }) => {
  const hostRef = useRef<HTMLSpanElement | null>(null);
  const [fit, setFit] = useState<{ width: number; regular: string; latest: string } | null>(null);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    let live = true;
    const apply = () => {
      if (!live) return;
      const width = el.clientWidth;
      const regular = labelFont(el);
      // ScoreTrend's last label (ScoreTrend.tsx:141).
      const latest = labelFont(el, "font-CircularStdBlack");
      setFit((prev) =>
        prev && prev.width === width && prev.regular === regular && prev.latest === latest ? prev : { width, regular, latest },
      );
    };
    apply();
    document.fonts?.ready.then(apply).catch(() => undefined);
    if (typeof ResizeObserver === "undefined") {
      return () => {
        live = false;
      };
    }
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      live = false;
      ro.disconnect();
    };
  }, []);

  const shown = useMemo(() => {
    if (!fit) return null;
    const labels = points.map((p) => dayMonth(p.date));
    const kept = trendLabelIndexes(labels, fit.width, (text, latest) =>
      labelWidth(text, latest ? fit.latest : fit.regular),
    );
    return new Set(kept.map((i) => points[i].date));
  }, [points, fit]);

  // Before the first measure (the same layout pass): the ends only.
  const formatLabel = (iso: string) => {
    const keep = shown ? shown.has(iso) : iso === points[0]?.date || iso === points[points.length - 1]?.date;
    return keep ? dayMonth(iso) : "";
  };

  return (
    // The latest point takes the band colour through currentColor.
    <span ref={hostRef} className={classNames("block min-w-0", scoreCls(overall))}>
      <ScoreTrend points={points} formatLabel={formatLabel} ariaLabel={trendAriaLabel(points)} />
    </span>
  );
};

/**
 * Overall (number, / 100, band chip, delta + "up from 70 on 10 Sep"), the
 * provenance line, then Human conversion and Agentic commerce with their
 * deltas. Staged by the page's ready-score count (the plan's "Score staging"):
 * 1 = the track and "First score…", 2–3 = the delta chip, 4+ = the delta chip
 * and the trend. `series` is the history once `[0]` is this row's latest
 * score (`inSyncHistory`); until then (loading, or the load failed) the
 * number shows on its track with nothing said about movement. The trend's
 * points are memoised on the series, so PageTrend's label fit holds across
 * the block's re-renders.
 */
export const ScoreBlock: FC<{ row: PdpUrl; view: PageView; series: PdpUrlScore[] | null }> = ({ row, view, series }) => {
  const score = row.latest_score as PdpUrlScore;
  const overall = score.overall_score as number;
  const stage = series ? scoreStage(series.length) : "track";
  const move = pageMovement(series);
  const points = useMemo(() => (stage === "trend" ? trendPoints(series) : []), [stage, series]);

  return (
    <>
      <div
        className="grid gap-x-10 gap-y-5 items-start min-w-0"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))" }}
      >
        <div className="flex flex-col gap-2.5 min-w-0">
          <Micro>Overall</Micro>
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-2">
            {/* The live headline's classes (PageScorePanel.tsx:498-506). */}
            <span className={classNames("font-CircularStdBlack tabular-nums leading-none text-[38px] sm:text-[44px]", scoreCls(overall))}>
              {overall}
            </span>
            <span className="text-text-2" style={{ fontSize: 13 }}>
              / 100
            </span>
            <span className="self-center inline-flex">
              <BandChip score={overall} />
            </span>
            {move && (
              <span className="self-center inline-flex flex-wrap items-center gap-2">
                <DeltaChip change={move.change} showZero />
                {/* No tabular-nums on prose: Circular's tabular spaces open the words up. */}
                <span className="text-text-2" style={{ fontSize: 12.5 }}>
                  {move.text}
                </span>
              </span>
            )}
          </div>
          {stage === "track" && (
            <span className={classNames("block", scoreCls(overall))}>
              <ScoreTrack value={overall} size="overall" />
            </span>
          )}
          {stage === "track" && series && (
            <span className="text-text-2" style={CAPTION}>
              First score. A trend appears as it&apos;s rescored.
            </span>
          )}
          <span className="text-text-2" style={CAPTION}>
            {provenanceLine(row, view)}
          </span>
        </div>
        {stage === "trend" && (
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <Micro>Overall, by score</Micro>
              <span className="text-text-2" style={CAPTION}>
                {points.length} scores
              </span>
            </div>
            <PageTrend points={points} overall={overall} />
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-[18px] border-t border-border-token pt-4 min-w-0">
        <SubScore
          label="Human conversion"
          question="Will a person buy from this page?"
          score={score.human_conversion_score ?? null}
          delta={subDelta(series, "human_conversion_score")}
        />
        <SubScore
          label="Agentic commerce"
          question="Can an AI shopping agent parse and trust it?"
          score={score.agentic_commerce_score ?? null}
          delta={subDelta(series, "agentic_commerce_score")}
          divided
        />
      </div>
    </>
  );
};
